import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Home, Users, CreditCard, BarChart3, Shield, LogOut, Check, X, ExternalLink, ChevronDown, Award, Coins, Bell, Tag, Mail, Brain, Send, ToggleLeft, DollarSign, Plus, Trash2, Save, FileText, MessageSquare, Settings as SettingsIcon } from "lucide-react";
import axios from "axios";
import PricingManager from "@/components/admin/PricingManager";
import BlogManager from "@/components/admin/BlogManager";
import InsightsManager from "@/components/admin/InsightsManager";
import AdminSettingsStrike3 from "@/components/admin/AdminSettingsStrike3";
import IntelligenceDashboard from "@/components/admin/IntelligenceDashboard";
import TaxonomyAdminTab from "@/components/admin/TaxonomyAdminTab";
import SecurityConsole from "@/components/admin/SecurityConsole";
import usePageTitle from "@/hooks/usePageTitle";
import { LogoDark } from "@/components/brand/Logo";

const API = process.env.REACT_APP_BACKEND_URL;

const tabs = [
  { id: "analytics", name: "Analytics", icon: BarChart3 },
  { id: "payments", name: "Payments", icon: CreditCard },
  { id: "users", name: "Users", icon: Users },
  { id: "mentors", name: "Mentors", icon: Award },
  { id: "sheleads", name: "She Leads", icon: Shield },
  { id: "offers", name: "Offers", icon: Tag },
  { id: "assessments", name: "Assessments", icon: Brain },
  { id: "coupons", name: "Discounts & Coupons", icon: Tag },
  { id: "pricing", name: "Pricing Manager", icon: DollarSign },
  { id: "blog", name: "Blog Manager", icon: FileText },
  { id: "insights", name: "Community Insights", icon: MessageSquare },
  { id: "intelligence", name: "Intelligence", icon: Brain },
  { id: "taxonomy", name: "Skills & Assessments", icon: FileText },
  { id: "security", name: "Security & Compliance", icon: Shield },
  { id: "settings", name: "Settings", icon: SettingsIcon },
];

function StatCard({ label, value, sub, color = "text-navy" }) {
  return (
    <div className="bg-white rounded-xl shadow-md p-5">
      <p className={`font-playfair text-3xl font-bold ${color}`}>{value}</p>
      <p className="font-dm text-navy/50 text-sm">{label}</p>
      {sub && <span className="font-dm text-navy/30 text-xs">{sub}</span>}
    </div>
  );
}

export default function AdminPage() {
  usePageTitle("Admin Panel");
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("analytics");
  const [analytics, setAnalytics] = useState(null);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [sheLeadsRequests, setSheLeadsRequests] = useState([]);
  const [mentorsList, setMentorsList] = useState([]);
  const [addTokenUserId, setAddTokenUserId] = useState("");
  const [addTokenAmount, setAddTokenAmount] = useState("");
  const [tokenMsg, setTokenMsg] = useState("");
  const [adminNotifs, setAdminNotifs] = useState([]);
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);
  const [femaleUsers, setFemaleUsers] = useState([]);
  const [offerActive, setOfferActive] = useState(false);
  const [offerDiscount, setOfferDiscount] = useState(20);
  const [offerExpiry, setOfferExpiry] = useState("");
  const [offerPacks, setOfferPacks] = useState([]);
  const [offerType, setOfferType] = useState("competency");
  const [dataLoading, setDataLoading] = useState(true);
  // Assessments tab state
  const [assessmentSubTab, setAssessmentSubTab] = useState("tree");
  const [treeSubmissions, setTreeSubmissions] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [assessmentPayments, setAssessmentPayments] = useState([]);
  const [featureToggles, setFeatureToggles] = useState({});
  const [reportDrafts, setReportDrafts] = useState({});
  const [tokenType, setTokenType] = useState("competency");
  // Coupons state
  const [coupons, setCoupons] = useState([]);
  const [packOverrides, setPackOverrides] = useState([]);
  const [discountCfg, setDiscountCfg] = useState({ global: { active: false, discount: 10, applies_to: "all", expiry: "" }, modules: {} });
  const [newCoupon, setNewCoupon] = useState({ code: "", discount: 20, applies_to: "all", uses_left: -1, expiry: "", status: "draft", note: "" });
  const [generatedCoupon, setGeneratedCoupon] = useState(null);

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/dashboard");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user?.role === "admin") { loadData(); loadNotifs(); }
  }, [user, activeTab]);

  async function loadNotifs() {
    try {
      const res = await axios.get(`${API}/api/admin/notifications`, { withCredentials: true });
      setAdminNotifs(res.data);
    } catch {}
  }

  async function loadData() {
    setDataLoading(true);
    try {
      if (activeTab === "analytics" && !analytics) {
        const res = await axios.get(`${API}/api/admin/analytics`, { withCredentials: true });
        setAnalytics(res.data);
      }
      if (activeTab === "payments") {
        const res = await axios.get(`${API}/api/admin/payments`, { withCredentials: true });
        setPayments(res.data);
      }
      if (activeTab === "users") {
        const res = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
        setUsers(res.data);
      }
      if (activeTab === "sheleads") {
        const res = await axios.get(`${API}/api/admin/sheleads-requests`, { withCredentials: true });
        setSheLeadsRequests(res.data);
        const res2 = await axios.get(`${API}/api/admin/female-users`, { withCredentials: true });
        setFemaleUsers(res2.data);
      }
      if (activeTab === "offers") {
        const res = await axios.get(`${API}/api/offers/active`);
        if (res.data.active) { setOfferActive(true); setOfferDiscount(res.data.discount); setOfferExpiry(res.data.expiry); setOfferPacks(res.data.packs || []); }
      }
      if (activeTab === "mentors") {
        const res = await axios.get(`${API}/api/admin/mentors`, { withCredentials: true });
        setMentorsList(res.data);
      }
      if (activeTab === "assessments") {
        const [treeRes, waitRes, payRes, togRes] = await Promise.all([
          axios.get(`${API}/api/admin/assessments/tree`, { withCredentials: true }),
          axios.get(`${API}/api/admin/assessments/waitlist`, { withCredentials: true }),
          axios.get(`${API}/api/admin/payments-assessment`, { withCredentials: true }),
          axios.get(`${API}/api/feature-toggles`),
        ]);
        setTreeSubmissions(treeRes.data);
        setWaitlist(waitRes.data);
        setAssessmentPayments(payRes.data);
        setFeatureToggles(togRes.data);
      }
      if (activeTab === "coupons") {
        const [cRes, cfgRes, oRes] = await Promise.all([
          axios.get(`${API}/api/admin/coupons`, { withCredentials: true }),
          axios.get(`${API}/api/admin/discounts/config`, { withCredentials: true }),
          axios.get(`${API}/api/admin/discounts/pack-overrides`, { withCredentials: true }),
        ]);
        setCoupons(cRes.data);
        setDiscountCfg(cfgRes.data || { global: { active: false, discount: 10, applies_to: "all", expiry: "" }, modules: {} });
        setPackOverrides(oRes.data);
      }
    } catch (err) {
      console.error("Admin data load error:", err);
    } finally {
      setDataLoading(false);
    }
  }

  async function approvePayment(id) {
    try {
      await axios.post(`${API}/api/admin/payments/${id}/approve`, {}, { withCredentials: true });
      setPayments(prev => prev.map(p => p._id === id ? { ...p, status: "approved" } : p));
    } catch { alert("Failed to approve"); }
  }

  async function approveSheLeads(userId) {
    try {
      await axios.post(`${API}/api/admin/sheleads/${userId}/approve`, {}, { withCredentials: true });
      setSheLeadsRequests(prev => prev.filter(r => r._id !== userId));
    } catch { alert("Failed to approve"); }
  }

  async function rejectSheLeads(userId) {
    try {
      await axios.post(`${API}/api/admin/sheleads/${userId}/reject`, {}, { withCredentials: true });
      setSheLeadsRequests(prev => prev.filter(r => r._id !== userId));
    } catch { alert("Failed to reject"); }
  }

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (loading || !user) return <div className="min-h-screen bg-cream flex items-center justify-center"><p className="font-dm text-navy/50">Loading...</p></div>;

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside className="w-64 bg-navy flex-shrink-0 flex flex-col fixed left-0 top-16 sidebar-scroll" style={{ height: "calc(100vh - 64px)", maxHeight: "calc(100vh - 64px)", overflowY: "auto", overflowX: "hidden" }} data-testid="admin-sidebar">
        <div className="p-6">
          <Link to="/" className="inline-flex items-center mb-6" style={{ textDecoration: "none" }}>
            <LogoDark width={170} height={40} />
            <span style={{
              fontFamily: "-apple-system,system-ui,Arial,sans-serif",
              fontSize: "9px",
              fontWeight: 500,
              background: "rgba(201,168,76,0.12)",
              color: "#C9A84C",
              padding: "3px 10px",
              borderRadius: "20px",
              marginLeft: "12px",
              letterSpacing: "1.5px",
              border: "0.5px solid rgba(201,168,76,0.25)",
              verticalAlign: "middle",
            }} data-testid="admin-badge">ADMIN</span>
          </Link>
          <nav className="space-y-1">
            {tabs.map((t) => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm text-sm transition-colors text-left ${activeTab === t.id ? "bg-gold/20 text-gold" : "text-white/70 hover:text-white hover:bg-white/5"}`}
              >
                <t.icon className="w-4 h-4" /> {t.name}
              </button>
            ))}
            <div className="h-px bg-white/10 my-3" />
            <Link to="/dashboard" className="flex items-center gap-3 px-4 py-2.5 rounded-lg font-dm text-sm text-white/70 hover:text-white hover:bg-white/5">
              <Home className="w-4 h-4" /> User Dashboard
            </Link>
          </nav>
        </div>
        <div className="mt-auto p-6 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-gold/20 rounded-full flex items-center justify-center">
              <span className="font-dm text-gold text-sm font-bold">{user.name?.[0]?.toUpperCase()}</span>
            </div>
            <div>
              <p className="font-dm text-white text-sm font-semibold">{user.name}</p>
              <span className="inline-block bg-red-500/20 text-red-300 font-dm text-[10px] px-2 py-0.5 rounded-full">Admin</span>
            </div>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-2 text-white/50 hover:text-white font-dm text-sm"><LogOut className="w-4 h-4" /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-64 bg-cream min-h-screen pt-16" data-testid="admin-main">
        <div className="bg-white px-8 py-5 border-b border-navy/5 flex items-center justify-between">
          <h1 className="font-playfair text-navy text-2xl">Admin Dashboard</h1>
          {/* Notification Bell */}
          <div className="relative">
            <button onClick={() => setShowNotifDropdown(!showNotifDropdown)} className="relative p-2 hover:bg-navy/5 rounded-lg transition-colors" data-testid="admin-bell">
              <Bell className="w-5 h-5 text-navy" />
              {adminNotifs.filter(n => !n.read).length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white font-dm text-[10px] font-bold rounded-full flex items-center justify-center">
                  {adminNotifs.filter(n => !n.read).length}
                </span>
              )}
            </button>
            {showNotifDropdown && (
              <div className="absolute right-0 top-12 bg-white rounded-xl shadow-xl border border-navy/10 w-80 max-h-96 overflow-y-auto z-50">
                <div className="flex items-center justify-between px-4 py-3 border-b border-navy/5">
                  <span className="font-dm text-navy font-semibold text-sm">Notifications</span>
                  <button onClick={async () => { await axios.post(`${API}/api/admin/notifications/read-all`, {}, { withCredentials: true }); loadNotifs(); }}
                    className="font-dm text-gold text-xs hover:underline">Mark all read</button>
                </div>
                {adminNotifs.length === 0 ? (
                  <p className="font-dm text-navy/40 text-xs p-4 text-center">No notifications</p>
                ) : (
                  adminNotifs.slice(0, 20).map(n => (
                    <div key={n._id} className={`px-4 py-3 border-b border-navy/5 ${!n.read ? 'bg-gold/5' : ''}`}>
                      <p className="font-dm text-navy text-sm font-semibold">{n.title}</p>
                      <p className="font-dm text-navy/60 text-xs">{n.message}</p>
                      <p className="font-dm text-navy/30 text-[10px] mt-1">{new Date(n.created_at).toLocaleString("en-IN")}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="p-8">
          {dataLoading ? (
            <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-4 border-gold border-t-transparent rounded-full animate-spin" /></div>
          ) : (
            <>
              {/* Analytics Tab */}
              {activeTab === "analytics" && analytics && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">User Overview</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Total Users" value={analytics.total_users} color="text-navy" />
                      <StatCard label="Starter (Free)" value={analytics.starter_users} color="text-navy" />
                      <StatCard label="Professional" value={analytics.professional_users} color="text-gold" />
                      <StatCard label="Enterprise" value={analytics.enterprise_users} color="text-gold" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Tokens & Revenue</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Total Tokens in Accounts" value={analytics.total_tokens_balance || 0} color="text-gold" />
                      <StatCard label="Tokens Consumed" value={analytics.total_tokens_used || 0} color="text-navy" />
                      <StatCard label="New Users This Week" value={analytics.new_users_week || 0} color="text-green-600" />
                      <StatCard label="Total Mentors" value={analytics.total_mentors || 0} color="text-navy" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Conversion & Payments</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Free Trial Users" value={analytics.free_trials} sub="anonymous visitors" />
                      <StatCard label="Conversion Rate" value={`${analytics.conversion_rate}%`} color="text-gold" sub="free to paid" />
                      <StatCard label="Pending Payments" value={analytics.pending_payments} color="text-orange-500" />
                      <StatCard label="Total Revenue" value={`\u20B9${(analytics.total_revenue || 0).toLocaleString()}`} color="text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Activity</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Maps Today" value={analytics.maps_today || 0} color="text-gold" />
                      <StatCard label="Maps Total" value={analytics.maps_month || 0} />
                      <StatCard label="Active Users (Week)" value={analytics.active_users_week || 0} color="text-green-600" />
                      <StatCard label="Popular Industry" value={analytics.popular_industry || "N/A"} color="text-navy" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Community & Engagement</h2>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <StatCard label="Total Posts" value={analytics.total_posts} />
                      <StatCard label="Newsletter Subscribers" value={analytics.total_newsletters} />
                      <StatCard label="She Leads Pending" value={analytics.sheleads_pending} color="text-orange-500" />
                      <StatCard label="She Leads Approved" value={analytics.sheleads_approved} color="text-green-600" />
                    </div>
                  </div>
                </div>
              )}

              {/* Payments Tab */}
              {activeTab === "payments" && (
                <div>
                  <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Payment Management</h2>
                  {payments.length === 0 ? (
                    <p className="font-dm text-navy/40 text-sm py-10 text-center">No payments yet</p>
                  ) : (
                    <div className="bg-white rounded-xl shadow-md overflow-hidden">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-navy/5 border-b border-navy/10">
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">User</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Plan</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Amount</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">UTR</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Status</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Date</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.map((p) => (
                            <tr key={p._id} className="border-b border-navy/5 hover:bg-cream/50">
                              <td className="font-dm text-navy text-sm py-3 px-4">{p.user_email}</td>
                              <td className="font-dm text-navy/70 text-sm py-3 px-4">{p.plan_name}</td>
                              <td className="font-dm text-navy font-semibold text-sm py-3 px-4">{"\u20B9"}{p.amount?.toLocaleString()}</td>
                              <td className="font-dm text-navy/70 text-xs py-3 px-4 font-mono">{p.utr || "-"}</td>
                              <td className="py-3 px-4">
                                <span className={`inline-block font-dm text-xs px-2 py-0.5 rounded-full ${
                                  p.status === "approved" ? "bg-green-100 text-green-700" :
                                  p.status === "submitted" ? "bg-yellow-100 text-yellow-700" :
                                  "bg-gray-100 text-gray-600"
                                }`}>{p.status}</span>
                              </td>
                              <td className="font-dm text-navy/50 text-xs py-3 px-4">{new Date(p.created_at).toLocaleDateString("en-IN")}</td>
                              <td className="py-3 px-4">
                                {(p.status === "submitted" || p.status === "pending") && (
                                  <button onClick={() => approvePayment(p._id)} data-testid={`approve-payment-${p._id}`}
                                    className="flex items-center gap-1 bg-green-500 text-white font-dm text-xs px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors">
                                    <Check className="w-3 h-3" /> Approve
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Users Tab */}
              {activeTab === "users" && (
                <div>
                  <h2 className="font-playfair text-navy text-xl font-semibold mb-4">All Users ({users.length})</h2>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-navy/5 border-b border-navy/10">
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Name</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Email</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Company</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Plan</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Tokens</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">A.Tokens</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Used</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Gender</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((u) => (
                          <tr key={u._id} className="border-b border-navy/5 hover:bg-cream/50">
                            <td className="font-dm text-navy text-sm py-3 px-4">{u.name}</td>
                            <td className="font-dm text-navy/70 text-sm py-3 px-4">{u.email}</td>
                            <td className="font-dm text-navy/70 text-sm py-3 px-4">{u.company || "-"}</td>
                            <td className="py-3 px-4">
                              <span className={`inline-block font-dm text-xs px-2 py-0.5 rounded-full capitalize ${
                                u.plan === "enterprise" ? "bg-gold/20 text-gold" :
                                u.plan === "professional" ? "bg-blue-100 text-blue-700" :
                                "bg-gray-100 text-gray-600"
                              }`}>{u.plan || "free"}</span>
                            </td>
                            <td className="font-dm text-gold font-semibold text-sm py-3 px-4">{u.tokens ?? 0}</td>
                            <td className="font-dm text-gold/70 font-semibold text-sm py-3 px-4">{u.assessment_tokens ?? 0}</td>
                            <td className="font-dm text-navy/70 text-sm py-3 px-4">{u.tokens_used ?? 0}</td>
                            <td className="font-dm text-navy/70 text-sm py-3 px-4 capitalize">{u.gender || "-"}</td>
                            <td className="font-dm text-navy/50 text-xs py-3 px-4">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Add Tokens */}
              {activeTab === "users" && (
                <div className="mt-6 bg-white rounded-xl shadow-md p-6 max-w-md">
                  <h3 className="font-dm text-navy font-bold mb-3">Add Tokens to User</h3>
                  <div className="flex gap-2 mb-2">
                    <input value={addTokenUserId} onChange={e => setAddTokenUserId(e.target.value)} placeholder="User email" type="email" className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                    <input value={addTokenAmount} onChange={e => setAddTokenAmount(e.target.value)} placeholder="Tokens" type="number" className="w-24 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                  </div>
                  <div className="flex gap-2">
                    <select value={tokenType} onChange={(e) => setTokenType(e.target.value)} className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                      <option value="competency">Map Tokens</option>
                      <option value="assessment">Assessment Tokens</option>
                    </select>
                    <button onClick={async () => {
                      try {
                        const res = await axios.post(`${API}/api/admin/add-tokens`, { email: addTokenUserId, tokens: parseInt(addTokenAmount), tokens_type: tokenType }, { withCredentials: true });
                        setTokenMsg(res.data.message + ` (balance: ${res.data.new_balance})`);
                        setAddTokenUserId(""); setAddTokenAmount("");
                        loadData();
                      } catch (err) { setTokenMsg(err?.response?.data?.detail || "Failed"); }
                    }} className="bg-gold text-navy font-dm font-bold px-4 py-2 rounded-lg text-sm">Add</button>
                  </div>
                  {tokenMsg && <p className="font-dm text-green-600 text-xs mt-2">{tokenMsg}</p>}
                </div>
              )}

              {/* Mentors Tab */}
              {activeTab === "mentors" && (
                <div>
                  <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Registered Mentors ({mentorsList.length})</h2>
                  {mentorsList.length === 0 ? (
                    <p className="font-dm text-navy/40 text-sm py-10 text-center">No mentors registered yet</p>
                  ) : (
                    <div className="space-y-4">
                      {mentorsList.map((m) => (
                        <div key={m._id} className="bg-white rounded-xl shadow-md p-6">
                          <div className="flex flex-col sm:flex-row justify-between gap-3">
                            <div>
                              <p className="font-dm text-navy font-bold">{m.full_name}</p>
                              <p className="font-dm text-navy/60 text-sm">{m.role_company} | {m.industry}</p>
                              <p className="font-dm text-navy/50 text-xs">{m.years_experience} years | {m.availability}</p>
                              {m.linkedin_url && <a href={m.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-gold text-xs hover:underline flex items-center gap-1 mt-1"><ExternalLink className="w-3 h-3" /> LinkedIn</a>}
                              {m.bio && <p className="font-dm text-navy/60 text-xs mt-2 max-w-md">{m.bio}</p>}
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {(m.expertise || []).map(e => <span key={e} className="bg-gold/10 text-gold font-dm text-[10px] px-2 py-0.5 rounded-full h-fit">{e}</span>)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* She Leads Requests */}
              {activeTab === "sheleads" && (
                <div className="space-y-8">
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">She Leads Access Requests ({sheLeadsRequests.length})</h2>
                    {sheLeadsRequests.length === 0 ? (
                      <p className="font-dm text-navy/40 text-sm py-4 text-center">No pending requests</p>
                    ) : (
                      <div className="space-y-4">
                        {sheLeadsRequests.map((r) => (
                          <div key={r._id} className="bg-white rounded-xl shadow-md p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div>
                              <p className="font-dm text-navy font-semibold">{r.name}</p>
                              <p className="font-dm text-navy/60 text-sm">{r.email}</p>
                              <p className="font-dm text-navy/50 text-xs">{r.job_title || ""} {r.company ? `at ${r.company}` : ""}</p>
                              {r.sheleads_linkedin && <a href={r.sheleads_linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-gold text-xs font-dm mt-1 hover:underline"><ExternalLink className="w-3 h-3" /> LinkedIn</a>}
                            </div>
                            <div className="flex items-center gap-2">
                              <button onClick={() => approveSheLeads(r._id)} className="flex items-center gap-1 bg-green-500 text-white font-dm text-sm px-4 py-2 rounded-lg hover:bg-green-600"><Check className="w-4 h-4" /> Approve</button>
                              <button onClick={() => rejectSheLeads(r._id)} className="flex items-center gap-1 bg-red-500 text-white font-dm text-sm px-4 py-2 rounded-lg hover:bg-red-600"><X className="w-4 h-4" /> Reject</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* Female Users */}
                  <div>
                    <h2 className="font-playfair text-navy text-xl font-semibold mb-4">All Female Users ({femaleUsers.length})</h2>
                    {femaleUsers.length > 0 && (
                      <div className="bg-white rounded-xl shadow-md overflow-hidden">
                        <table className="w-full">
                          <thead><tr className="bg-navy/5 border-b border-navy/10">
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Name</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Email</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Joined</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">She Leads</th>
                            <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Action</th>
                          </tr></thead>
                          <tbody>
                            {femaleUsers.map(u => (
                              <tr key={u._id} className="border-b border-navy/5">
                                <td className="font-dm text-navy text-sm py-3 px-4">{u.name}</td>
                                <td className="font-dm text-navy/70 text-sm py-3 px-4">{u.email}</td>
                                <td className="font-dm text-navy/50 text-xs py-3 px-4">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "-"}</td>
                                <td className="py-3 px-4"><span className={`font-dm text-xs px-2 py-0.5 rounded-full ${u.sheleads_access === "approved" ? "bg-green-100 text-green-700" : u.sheleads_invited ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>{u.sheleads_access === "approved" ? "Active" : u.sheleads_invited ? "Invited" : "Not visited"}</span></td>
                                <td className="py-3 px-4">
                                  {!u.sheleads_invited && u.sheleads_access !== "approved" && (
                                    <button onClick={async () => { await axios.post(`${API}/api/admin/invite-sheleads/${u._id}`, {}, { withCredentials: true }); setFemaleUsers(prev => prev.map(fu => fu._id === u._id ? {...fu, sheleads_invited: true} : fu)); }}
                                      className="flex items-center gap-1 text-gold font-dm text-xs hover:underline"><Mail className="w-3 h-3" /> Invite</button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Offers Tab */}
              {activeTab === "offers" && (
                <div className="max-w-lg">
                  <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Global Offer Settings</h2>
                  <div className="bg-white rounded-xl shadow-md p-8 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-dm text-navy font-semibold">Offer Type</span>
                      <select value={offerType} onChange={(e) => setOfferType(e.target.value)} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                        <option value="competency">Competency Maps</option>
                        <option value="assessment">Assessment Tokens</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-dm text-navy font-semibold">Offer Active</span>
                      <button onClick={() => setOfferActive(!offerActive)}
                        className={`relative w-14 h-7 rounded-full transition-colors ${offerActive ? "bg-gold" : "bg-navy/20"}`}>
                        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${offerActive ? "left-8" : "left-1"}`} />
                      </button>
                    </div>
                    {offerActive && (
                      <>
                        <div>
                          <label className="font-dm text-navy text-sm font-semibold">Discount %</label>
                          <select value={offerDiscount} onChange={e => setOfferDiscount(Number(e.target.value))} className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm">
                            {[10, 15, 20, 25, 30, 50].map(d => <option key={d} value={d}>{d}%</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="font-dm text-navy text-sm font-semibold">Expiry Date</label>
                          <input type="datetime-local" value={offerExpiry} onChange={e => setOfferExpiry(e.target.value)} className="w-full mt-1 border border-navy/20 rounded-lg px-4 py-2.5 font-dm text-sm" />
                        </div>
                        <div>
                          <label className="font-dm text-navy text-sm font-semibold">Applies to (leave empty for all)</label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {(offerType === "assessment"
                              ? ["Assessment Starter", "Assessment Growth", "Assessment Pro", "Assessment Power", "Assessment Team", "Assessment Business", "Assessment Enterprise"]
                              : ["Starter Pack", "Growth Pack", "Pro Pack", "Power Pack"]
                            ).map(p => (
                              <button key={p} type="button" onClick={() => setOfferPacks(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])}
                                className={`font-dm text-xs px-3 py-1.5 rounded-full ${offerPacks.includes(p) ? "bg-gold text-navy font-semibold" : "bg-navy/5 text-navy/60"}`}>{p}</button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                    <button onClick={async () => {
                      await axios.post(`${API}/api/admin/offers`, { active: offerActive, discount: offerDiscount, packs: offerPacks, expiry: offerExpiry, type: offerType }, { withCredentials: true });
                      alert(offerActive ? `${offerType === "assessment" ? "Assessment" : "Competency"} offer activated!` : "Offer deactivated!");
                    }} className="w-full bg-navy text-white font-dm font-bold py-3 rounded-lg hover:bg-navy/90 transition-colors">
                      Save Offer Settings
                    </button>
                  </div>
                </div>
              )}

              {/* Assessments Tab */}
              {activeTab === "assessments" && (
                <div className="space-y-6">
                  {/* Sub-tabs */}
                  <div className="flex gap-1 bg-white rounded-lg p-1 w-fit shadow-sm flex-wrap">
                    {[
                      { id: "tree", name: "Tree Submissions", count: treeSubmissions.filter(t => t.status === "pending_analysis").length },
                      { id: "payments", name: "Assessment Payments", count: assessmentPayments.filter(p => p.status !== "approved").length },
                      { id: "waitlist", name: "Waitlist" },
                      { id: "toggles", name: "Feature Toggles" },
                    ].map((t) => (
                      <button key={t.id} onClick={() => setAssessmentSubTab(t.id)}
                        className={`font-dm text-sm px-4 py-2 rounded-lg transition-colors ${assessmentSubTab === t.id ? "bg-navy text-white" : "text-navy/50 hover:text-navy"}`}
                        data-testid={`assessment-subtab-${t.id}`}>
                        {t.name}{t.count > 0 ? <span className="ml-2 bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{t.count}</span> : null}
                      </button>
                    ))}
                  </div>

                  {/* Tree Submissions */}
                  {assessmentSubTab === "tree" && (
                    <div>
                      <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Tree Analysis Submissions ({treeSubmissions.length})</h2>
                      {treeSubmissions.length === 0 ? (
                        <p className="font-dm text-navy/40 text-sm py-8 text-center">No submissions yet</p>
                      ) : (
                        <div className="space-y-4">
                          {treeSubmissions.map((s) => (
                            <div key={s._id} className="bg-white rounded-xl shadow-md p-5" data-testid={`tree-submission-${s._id}`}>
                              <div className="flex flex-col md:flex-row gap-4">
                                {s.drawing_url && (
                                  <a href={`${API}${s.drawing_url}`} target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                                    <img src={`${API}${s.drawing_url}`} alt="Tree drawing" className="w-32 h-32 object-cover rounded-lg border-2 border-gold/30 hover:border-gold transition-colors" />
                                  </a>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    <p className="font-dm text-navy font-bold text-sm">{s.user_name}</p>
                                    <span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${s.status === "report_sent" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                                      {s.status === "report_sent" ? "Report Sent" : "Pending Analysis"}
                                    </span>
                                  </div>
                                  <p className="font-dm text-navy/60 text-xs">{s.user_email}</p>
                                  <p className="font-dm text-navy/50 text-xs">{s.current_role || "—"} {s.company ? `at ${s.company}` : ""} · {s.age_range || "Age N/A"}</p>
                                  <p className="font-dm text-navy/40 text-[10px] mt-1">{new Date(s.created_at).toLocaleString("en-IN")}</p>
                                  {s.notes && <p className="font-dm text-navy/60 text-xs mt-2 italic bg-cream p-2 rounded">"{s.notes}"</p>}
                                  {s.report ? (
                                    <div className="mt-3 bg-green-50 border border-green-200 rounded p-3">
                                      <p className="font-dm text-green-800 text-xs font-semibold mb-1">Report sent {s.report_sent_at ? `on ${new Date(s.report_sent_at).toLocaleDateString("en-IN")}` : ""}</p>
                                      <p className="font-dm text-navy/70 text-xs whitespace-pre-wrap">{s.report.slice(0, 200)}{s.report.length > 200 ? "…" : ""}</p>
                                    </div>
                                  ) : (
                                    <div className="mt-3 space-y-2">
                                      <textarea value={reportDrafts[s._id] || ""} onChange={(e) => setReportDrafts({ ...reportDrafts, [s._id]: e.target.value })}
                                        placeholder="Write the personalised analysis report here…" rows={5} data-testid={`tree-report-input-${s._id}`}
                                        className="w-full border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm focus:border-gold focus:outline-none" />
                                      <button onClick={async () => {
                                        const text = (reportDrafts[s._id] || "").trim();
                                        if (text.length < 30) { alert("Report must be at least 30 characters"); return; }
                                        try {
                                          await axios.post(`${API}/api/admin/assessments/tree/${s._id}/report`, { report: text }, { withCredentials: true });
                                          loadData();
                                        } catch { alert("Failed to send report"); }
                                      }} data-testid={`tree-send-report-${s._id}`}
                                        className="bg-navy text-gold font-dm font-bold text-xs px-4 py-2 rounded-lg hover:bg-navy/90 transition-colors flex items-center gap-2">
                                        <Send className="w-3 h-3" /> Send Report to User
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assessment Payments */}
                  {assessmentSubTab === "payments" && (
                    <div>
                      <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Assessment Payment Requests ({assessmentPayments.length})</h2>
                      {assessmentPayments.length === 0 ? (
                        <p className="font-dm text-navy/40 text-sm py-8 text-center">No assessment payments yet</p>
                      ) : (
                        <div className="bg-white rounded-xl shadow-md overflow-hidden">
                          <table className="w-full">
                            <thead><tr className="bg-navy/5 border-b border-navy/10">
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">User</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Pack</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Type</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Amount</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Tokens</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Status</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Date</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Action</th>
                            </tr></thead>
                            <tbody>
                              {assessmentPayments.map((p) => (
                                <tr key={p._id} className="border-b border-navy/5 hover:bg-cream/50">
                                  <td className="font-dm text-navy text-sm py-3 px-4">{p.user_email}</td>
                                  <td className="font-dm text-navy/70 text-sm py-3 px-4">{p.pack_name}</td>
                                  <td className="py-3 px-4">
                                    <span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${p.tokens_type === "bundle" ? "bg-purple-100 text-purple-700" : "bg-gold/20 text-gold"}`}>
                                      {p.tokens_type === "bundle" ? "Bundle" : "Assessment"}
                                    </span>
                                  </td>
                                  <td className="font-dm text-navy font-semibold text-sm py-3 px-4">₹{p.amount?.toLocaleString()}</td>
                                  <td className="font-dm text-navy/70 text-xs py-3 px-4">
                                    {p.tokens}{p.bundle_assessment_tokens > 0 ? ` + ${p.bundle_assessment_tokens} A` : ""}
                                  </td>
                                  <td className="py-3 px-4">
                                    <span className={`inline-block font-dm text-xs px-2 py-0.5 rounded-full ${p.status === "approved" ? "bg-green-100 text-green-700" : p.status === "submitted" ? "bg-yellow-100 text-yellow-700" : "bg-gray-100 text-gray-600"}`}>{p.status}</span>
                                  </td>
                                  <td className="font-dm text-navy/50 text-xs py-3 px-4">{new Date(p.created_at).toLocaleDateString("en-IN")}</td>
                                  <td className="py-3 px-4">
                                    {p.status !== "approved" && (
                                      <button onClick={() => approvePayment(p._id)} data-testid={`approve-assessment-payment-${p._id}`}
                                        className="flex items-center gap-1 bg-green-500 text-white font-dm text-xs px-3 py-1.5 rounded-lg hover:bg-green-600 transition-colors">
                                        <Check className="w-3 h-3" /> Approve
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Waitlist */}
                  {assessmentSubTab === "waitlist" && (
                    <div>
                      <h2 className="font-playfair text-navy text-xl font-semibold mb-4">Coming Soon Waitlist ({waitlist.length})</h2>
                      {waitlist.length === 0 ? (
                        <p className="font-dm text-navy/40 text-sm py-8 text-center">No waitlist signups yet</p>
                      ) : (
                        <div className="bg-white rounded-xl shadow-md overflow-hidden">
                          <table className="w-full">
                            <thead><tr className="bg-navy/5 border-b border-navy/10">
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Email</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Assessment</th>
                              <th className="text-left font-dm text-navy/60 text-xs py-3 px-4">Date</th>
                            </tr></thead>
                            <tbody>
                              {waitlist.map((w) => (
                                <tr key={w._id} className="border-b border-navy/5">
                                  <td className="font-dm text-navy text-sm py-3 px-4">{w.email}</td>
                                  <td className="py-3 px-4"><span className="font-dm text-xs uppercase bg-gold/10 text-gold px-2 py-0.5 rounded-full">{w.assessment}</span></td>
                                  <td className="font-dm text-navy/50 text-xs py-3 px-4">{new Date(w.created_at).toLocaleDateString("en-IN")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Feature Toggles */}
                  {assessmentSubTab === "toggles" && (
                    <div>
                      <h2 className="font-playfair text-navy text-xl font-semibold mb-2 flex items-center gap-2">
                        <ToggleLeft className="w-5 h-5 text-gold" /> Assessment Feature Toggles
                      </h2>
                      <p className="font-dm text-navy/60 text-sm mb-6">Control which assessments appear LIVE, COMING SOON, or are hidden (DISABLED).</p>
                      <div className="bg-white rounded-xl shadow-md p-6 space-y-3 max-w-2xl">
                        {["axis", "tree", "hbdi", "hogan", "mbti", "true_colors"].map((key) => (
                          <div key={key} className="flex items-center justify-between border-b border-navy/5 last:border-b-0 py-3" data-testid={`toggle-row-${key}`}>
                            <span className="font-dm text-navy font-semibold capitalize">{key.replace("_", " ")}</span>
                            <select value={featureToggles[key] || "LIVE"}
                              onChange={async (e) => {
                                const newStatus = e.target.value;
                                setFeatureToggles({ ...featureToggles, [key]: newStatus });
                                await axios.post(`${API}/api/admin/feature-toggles`, { assessment: key, status: newStatus }, { withCredentials: true });
                              }}
                              data-testid={`toggle-${key}`}
                              className="border border-navy/20 rounded-lg px-3 py-1.5 font-dm text-sm focus:border-gold focus:outline-none">
                              <option value="LIVE">LIVE</option>
                              <option value="COMING_SOON">COMING SOON</option>
                              <option value="DISABLED">DISABLED</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Discounts & Coupons Tab */}
              {activeTab === "coupons" && (
                <div className="space-y-8" data-testid="coupons-tab">
                  {/* Global */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="font-playfair text-navy text-lg font-bold mb-1">Global Discount</h2>
                    <p className="font-dm text-navy/60 text-xs mb-4">Apply a % discount across all packs simultaneously.</p>
                    <div className="flex flex-wrap items-center gap-4 mb-4">
                      <button onClick={() => setDiscountCfg({ ...discountCfg, global: { ...discountCfg.global, active: !discountCfg.global?.active } })}
                        data-testid="global-discount-toggle"
                        className={`relative w-14 h-7 rounded-full transition-colors ${discountCfg.global?.active ? "bg-gold" : "bg-navy/20"}`}>
                        <span className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${discountCfg.global?.active ? "left-8" : "left-1"}`} />
                      </button>
                      <span className="font-dm text-navy text-sm font-semibold">{discountCfg.global?.active ? "ON" : "OFF"}</span>
                    </div>
                    {discountCfg.global?.active && (
                      <div className="grid sm:grid-cols-3 gap-3">
                        <input type="number" min="1" max="60" value={discountCfg.global.discount || 10} onChange={(e) => setDiscountCfg({ ...discountCfg, global: { ...discountCfg.global, discount: Number(e.target.value) } })}
                          className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" placeholder="Discount %" />
                        <select value={discountCfg.global.applies_to || "all"} onChange={(e) => setDiscountCfg({ ...discountCfg, global: { ...discountCfg.global, applies_to: e.target.value } })}
                          className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                          <option value="all">All packs</option>
                          <option value="intelligence">Intelligence only</option>
                          <option value="assessment">Assessment only</option>
                          <option value="coaching">Coaching only</option>
                          <option value="capscan">CapScan only</option>
                          <option value="bundles">Bundles only</option>
                        </select>
                        <input type="date" value={(discountCfg.global.expiry || "").slice(0, 10)} onChange={(e) => setDiscountCfg({ ...discountCfg, global: { ...discountCfg.global, expiry: e.target.value } })}
                          className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                      </div>
                    )}
                    <button onClick={async () => {
                      await axios.post(`${API}/api/admin/discounts/global`, discountCfg.global, { withCredentials: true });
                      alert("Global discount saved");
                    }} data-testid="save-global-discount" className="mt-4 bg-navy text-gold font-dm font-bold px-5 py-2 rounded-lg text-sm">Apply Discount</button>
                  </div>

                  {/* Module toggles */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="font-playfair text-navy text-lg font-bold mb-1">Module-Level Discounts</h2>
                    <p className="font-dm text-navy/60 text-xs mb-4">Each toggle is independent.</p>
                    <div className="divide-y divide-navy/5">
                      {["intelligence", "assessment", "coaching", "capscan", "bundles", "sheleads"].map((mod) => {
                        const m = discountCfg.modules?.[mod] || { active: false, discount: 10 };
                        return (
                          <div key={mod} className="flex flex-wrap items-center gap-3 py-3" data-testid={`module-${mod}`}>
                            <div className="flex-1 min-w-[140px]">
                              <p className="font-dm text-navy text-sm font-bold capitalize">{mod === "sheleads" ? "She Leads" : mod} packs</p>
                              {mod === "sheleads" && <p className="font-dm text-navy/50 text-[10px]">Visible only to gender-verified She Leads users.</p>}
                            </div>
                            <input type="number" min="1" max="60" value={m.discount} onChange={(e) => setDiscountCfg({ ...discountCfg, modules: { ...discountCfg.modules, [mod]: { ...m, discount: Number(e.target.value) } } })}
                              className="border border-navy/20 rounded-lg px-2 py-1 font-dm text-sm w-20" />
                            <span className="font-dm text-navy/50 text-xs">%</span>
                            <button onClick={async () => {
                              const updated = { ...m, active: !m.active };
                              setDiscountCfg({ ...discountCfg, modules: { ...discountCfg.modules, [mod]: updated } });
                              await axios.post(`${API}/api/admin/discounts/module`, { module: mod, ...updated }, { withCredentials: true });
                            }} className={`relative w-12 h-6 rounded-full ${m.active ? "bg-gold" : "bg-navy/20"}`}>
                              <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${m.active ? "left-6" : "left-0.5"}`} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Promo Codes */}
                  <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="font-playfair text-navy text-lg font-bold mb-1">Active Promo Codes ({coupons.length})</h2>
                    <p className="font-dm text-navy/60 text-xs mb-4">All codes, settings, and usage status.</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b border-navy/10 bg-navy/5">
                          <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Code</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">%</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Applies</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Uses left</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Status</th>
                          <th className="text-left font-dm text-navy/60 text-xs py-2 px-3">Actions</th>
                        </tr></thead>
                        <tbody>
                          {coupons.map((c) => {
                            const statusColor = { live: "bg-green-100 text-green-700", scheduled: "bg-blue-100 text-blue-700", draft: "bg-gray-100 text-gray-600", expired: "bg-red-100 text-red-700", paused: "bg-amber-100 text-amber-700" };
                            return (
                              <tr key={c._id} className="border-b border-navy/5" data-testid={`coupon-row-${c.code}`}>
                                <td className="font-dm text-navy font-bold py-2 px-3"><code>{c.code}</code></td>
                                <td className="font-dm text-navy/70 py-2 px-3">{c.discount}%</td>
                                <td className="font-dm text-navy/70 py-2 px-3 text-xs capitalize">{c.applies_to}</td>
                                <td className="font-dm text-navy/70 py-2 px-3">{c.uses_left === -1 ? "∞" : c.uses_left}</td>
                                <td className="py-2 px-3"><span className={`font-dm text-[10px] px-2 py-0.5 rounded-full ${statusColor[c.status] || "bg-gray-100"}`}>{c.status}</span></td>
                                <td className="py-2 px-3 space-x-2">
                                  <select value={c.status} onChange={async (e) => { await axios.patch(`${API}/api/admin/coupons/${c._id}`, { status: e.target.value }, { withCredentials: true }); loadData(); }} className="border border-navy/20 rounded px-1 py-0.5 font-dm text-xs">
                                    <option value="live">Live</option><option value="draft">Draft</option><option value="paused">Paused</option>
                                  </select>
                                  <button onClick={async () => { if (window.confirm("Delete this coupon?")) { await axios.delete(`${API}/api/admin/coupons/${c._id}`, { withCredentials: true }); loadData(); } }}
                                    data-testid={`delete-coupon-${c.code}`} className="text-red-500 hover:text-red-700 text-xs">Delete</button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Generator */}
                  <div className="bg-white rounded-xl shadow-md p-6" data-testid="coupon-generator">
                    <h2 className="font-playfair text-navy text-lg font-bold mb-1">⚡ Generate New Coupon</h2>
                    <p className="font-dm text-navy/60 text-xs mb-4">Create a new promo code and add it to the list above.</p>

                    {/* Quick generate */}
                    <div className="flex flex-wrap gap-2 mb-5">
                      {[
                        { label: "Demo 50% — 1 use", c: "DEMO50", d: 50, a: "bundles", u: 1, s: "draft" },
                        { label: "Launch 20% — All packs", c: "LAUNCH20", d: 20, a: "all", u: -1, s: "live" },
                        { label: "Trial 30% — First buy", c: "TRIAL30", d: 30, a: "first_purchase", u: 1, s: "draft" },
                        { label: "Coaching 25% — 100 uses", c: "COACH25", d: 25, a: "coaching", u: 100, s: "draft" },
                      ].map((q) => (
                        <button key={q.c} onClick={() => setNewCoupon({ code: q.c, discount: q.d, applies_to: q.a, uses_left: q.u, expiry: "", status: q.s, note: "" })}
                          data-testid={`quick-gen-${q.c}`} className="bg-navy/5 hover:bg-navy/10 text-navy font-dm text-xs font-bold px-3 py-2 rounded-lg">
                          {q.label}
                        </button>
                      ))}
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3 mb-3">
                      <div className="flex gap-2">
                        <input value={newCoupon.code} onChange={(e) => setNewCoupon({ ...newCoupon, code: e.target.value.toUpperCase() })} placeholder="APEX30"
                          data-testid="coupon-code-input" className="flex-1 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm font-mono uppercase" />
                        <button onClick={async () => { const r = await axios.post(`${API}/api/admin/coupons/random`, {}, { withCredentials: true }); setNewCoupon({ ...newCoupon, code: r.data.code }); }}
                          data-testid="random-code-btn" className="bg-gold/20 text-gold font-dm text-xs font-bold px-3 py-2 rounded-lg whitespace-nowrap">Random</button>
                      </div>
                      <input type="number" min="1" max="60" value={newCoupon.discount} onChange={(e) => setNewCoupon({ ...newCoupon, discount: Number(e.target.value) })}
                        placeholder="Discount %" className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                      <select value={newCoupon.applies_to} onChange={(e) => setNewCoupon({ ...newCoupon, applies_to: e.target.value })} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                        <option value="all">All packs</option><option value="intelligence">Intelligence</option><option value="assessment">Assessment</option><option value="coaching">Coaching</option><option value="capscan">CapScan</option><option value="bundles">Bundles</option><option value="first_purchase">First purchase</option>
                      </select>
                      <select value={newCoupon.uses_left} onChange={(e) => setNewCoupon({ ...newCoupon, uses_left: Number(e.target.value) })} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                        {[1, 5, 10, 25, 50, 100].map((n) => <option key={n} value={n}>{n} uses</option>)}
                        <option value={-1}>Unlimited</option>
                      </select>
                      <input type="date" value={newCoupon.expiry?.slice(0, 10) || ""} onChange={(e) => setNewCoupon({ ...newCoupon, expiry: e.target.value })} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                      <select value={newCoupon.status} onChange={(e) => setNewCoupon({ ...newCoupon, status: e.target.value })} className="border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm">
                        <option value="live">Activate now</option><option value="draft">Save as draft</option><option value="scheduled">Schedule</option>
                      </select>
                      <input value={newCoupon.note} onChange={(e) => setNewCoupon({ ...newCoupon, note: e.target.value })} placeholder="Internal note (admin only)" className="sm:col-span-2 border border-navy/20 rounded-lg px-3 py-2 font-dm text-sm" />
                    </div>
                    <div className="flex gap-3">
                      <button onClick={async () => {
                        try {
                          await axios.post(`${API}/api/admin/coupons`, newCoupon, { withCredentials: true });
                          setGeneratedCoupon(newCoupon);
                          loadData();
                        } catch (e) { alert(e?.response?.data?.detail || "Failed"); }
                      }} data-testid="generate-coupon-btn" className="bg-navy text-gold font-dm font-bold px-5 py-2.5 rounded-lg text-sm">Generate Coupon</button>
                      <button onClick={() => setNewCoupon({ code: "", discount: 20, applies_to: "all", uses_left: -1, expiry: "", status: "draft", note: "" })} className="border border-navy text-navy font-dm font-bold px-5 py-2.5 rounded-lg text-sm">Clear Form</button>
                    </div>

                    {generatedCoupon && (
                      <div className="mt-5 bg-navy rounded-xl p-5 text-center" data-testid="generated-coupon-preview">
                        <p className="font-mono font-bold text-gold text-3xl mb-2 tracking-wider">{generatedCoupon.code}</p>
                        <p className="font-dm text-white/70 text-xs">{generatedCoupon.discount}% off · {generatedCoupon.applies_to} · {generatedCoupon.uses_left === -1 ? "Unlimited" : `${generatedCoupon.uses_left} uses`} · {generatedCoupon.status}</p>
                        <div className="flex gap-2 justify-center mt-3">
                          <button onClick={() => { navigator.clipboard.writeText(generatedCoupon.code); alert("Copied!"); }} className="bg-gold text-navy font-dm font-bold text-xs px-4 py-1.5 rounded-lg">Copy Code</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {activeTab === "pricing" && <PricingManager />}
          {activeTab === "blog" && <BlogManager />}
          {activeTab === "insights" && <InsightsManager />}
          {activeTab === "intelligence" && <IntelligenceDashboard />}
          {activeTab === "taxonomy" && <TaxonomyAdminTab />}
          {activeTab === "security" && <SecurityConsole />}
          {activeTab === "settings" && <AdminSettingsStrike3 />}
        </div>
      </main>
    </div>
  );
}
